import React, { useState, useCallback, useEffect } from 'react'
import styled from 'styled-components'
import { Heading, Card, CardHeader, CardBody, Button, Text, Flex, useMatchBreakpoints } from 'penguinfinance-uikit2'
import { useWeb3React } from '@web3-react/core'
import BigNumber from 'bignumber.js'
import useI18n from 'hooks/useI18n'
import { useDispatch } from 'react-redux'
import partition from 'lodash/partition'
import { useAllHarvest } from 'hooks/useHarvest'
import UnlockButton from 'components/UnlockButton'
import { getBalanceNumber } from 'utils/formatBalance'
import { useV2Farms, usePricePefiUsdt, usePricePngUsdt, useV2Pools } from 'state/hooks'
import { fetchV2FarmUserDataAsync } from 'state/actions'
import useAvaxPrice from 'hooks/useAvaxPrice';
import { getAddress } from 'utils/addressHelpers'
import tokens from 'config/constants/tokens'
import useJoePrice from 'hooks/useJoePrice'
import useTokenPrice from 'hooks/useTokenPrice'
import useHighestIglooApr from 'hooks/useHighestIglooApr';
import useRefresh from 'hooks/useRefresh'
import roundDown from 'utils/roundDown'
import PefiHarvestBalance from './PefiHarvestBalance'
import PefiWalletBalance from './PefiWalletBalance'
import InfoCard from './InfoCard'

const StyledFarmStakingCard = styled(Card)`
  background: ${({ theme }) => theme.isDark && '#272044'};
  border-radius: 10px;
  max-width: 1300px;
  width: 100%;
  margin-top: 4px;

  @media (min-width: 768px) {
    margin-top: 24px;
  }
  @media (min-width: 1200px) {
    margin-top: 40px;
  }
`

const Label = styled(Text).attrs({ color: 'red' })`
  line-height: 1;
  font-size: 16px;
  color: ${({ theme }) => (theme.isDark ? '#ffffff' : '#000000')};
  font-style: normal;
  font-weight: 300;
  line-height: 24px;
  font-family: 'Kanit';

  @media (min-width: 1200px) {
    font-size: 22px;
    line-height: 34px;
  }
`

const StyledButton = styled(Button)`
  background-color: ${({ theme }) => theme.isDark ? '#483692' : '#392771'};
  color: white;
  width: 100%;
  border-radius: 10px;
  white-space: nowrap;
  margin-bottom: 8px;
  font-weight: 500;
  margin-top: 6px;
  box-shadow: none;
  margin-left: 4px;
  margin-right: 4px;
  font-size: 20px;
  height: 44px;
  max-width: 300px;

  img {
    margin-left: 8px;
  }

  &:last-child {
    margin-bottom: 0;
  }

  @media (min-width: 640px) {
    width: 50%;
    margin-bottom: 0;
    margin-left: 0;
    margin-right: 0;
  }
  @media (min-width: 1200px) {
    height: 50px;
    font-size: 24px;
  }
`
const Title = styled(Heading)`
  font-family: Kanit;
  font-style: normal;
  font-weight: bold;
  font-size: 22px;
  line-height: 48px;
  color: white;

  @media (min-width: 768px) {
    font-size: 28px;
    line-height: 56px;
  }
  @media (min-width: 968px) {
    font-size: 36px;
    line-height: 56px;
  }
  @media (min-width: 1200px) {
    font-size: 44px;
    line-height: 75px;
  }
`

const StyledFlex = styled(Flex)`
  align-items: center;
`

const StyledCardHeader = styled(CardHeader)`
  padding-top: 0px;
  padding-bottom: 0px;
  display: flex;
  justify-content: center;
  background: ${({ theme }) => (theme.isDark ? '#483692' : '#EE3E40')};

  @media (min-width: 968px) {
    padding-left: 56px;
    justify-content: flex-start;
  }
`

const StyledUnlockButton = styled(UnlockButton)`
  background-color: ${({ theme }) => theme.isDark ? '#483692' : '#392771'};
  color: white;
  width: 100%;
  border-radius: 10px;
  white-space: nowrap;
  margin-bottom: 8px;
  font-weight: 500;
  margin-top: 6px;
  height: 44px;
  max-width: 300px;

  img {
    margin-left: 8px;
  }

  &:last-child {
    margin-bottom: 0;
  }

  @media (min-width: 640px) {
    width: 50%;
    margin-bottom: 0;
  }
  @media (min-width: 1200px) {
    height: 50px;
    font-size: 24px;
  }
`

const StyledCardBody = styled(CardBody)`
  padding: 28px 16px;

  @media (min-width: 768px) {
    padding: 24px 20px;
  }
  @media (min-width: 968px) {
    padding: 24px 40px;
  }
  @media (min-width: 1200px) {
    padding: 28px 56px;
  }
`;

const BalanceText = styled(Text)`
  color: ${({ theme }) => (theme.isDark ? '#fff' : '#000')};
  font-weight: 600;
  line-height: 1.2;
  font-size: 30px;

  @media (min-width: 1200px) {
    font-size: 32px;
  }
`

const PefiPrices = styled(Flex)`
  padding-left: 24px;
  @media (min-width: 1200px) {
    padding-left: 0;
  }
`;

const FarmedStakingCard = ({ pool }) => {
  const [pendingTx, setPendingTx] = useState(false)
  const { account } = useWeb3React()
  const TranslateString = useI18n()
  const { isXl } = useMatchBreakpoints()
  const isMobile = !isXl
  const { userData } = pool
  const stakedBalance = new BigNumber(userData?.stakedBalance || 0)
  const v2Farms = useV2Farms()
  const { fastRefresh } = useRefresh()
  const dispatch = useDispatch()
  
  const pngPriceUsd = usePricePngUsdt().toNumber()
  const joePriceUsd = useJoePrice()
  const avaxPriceUsd = useAvaxPrice();
  const iPefiToPefiRatio = pool.currentExchangeRate || 1
  const pefiPriceUsd = usePricePefiUsdt().toNumber()
  const iPefiPriceUsd = iPefiToPefiRatio * pefiPriceUsd
  const { lydPrice: lydPriceUsd, sushiPrice: sushiPriceUsd, qiPrice: qiPriceUsd } = useTokenPrice()  
  const v2FarmsWithRewards = v2Farms.filter((row) => row.userData && Number(row.userData.stakedBalance) > 0)
  const { onReward } = useAllHarvest(v2FarmsWithRewards.map((farmWithBalance) => farmWithBalance.pid))

  const pools = useV2Pools(account);
  const [, openPools] = partition(pools, openPool => openPool.isFinished)
  const nestPool = openPools[0]
  const avgDailyAprPerWeek = nestPool.avgDailyAprPerWeek || 0
  const avgYearlyApr = avgDailyAprPerWeek * 365 * 100
  const iglooApr = useHighestIglooApr();

  const harvestAllFarms = useCallback(async () => {
    setPendingTx(true)
    try {
      await onReward()
    } catch (error) {
      // TODO: find a way to handle when the user rejects transaction or it fails
    } finally {
      setPendingTx(false)
    }
  }, [onReward])

  useEffect(() => {
    if (account) {
      dispatch(fetchV2FarmUserDataAsync(account))
    }
  }, [account, dispatch, fastRefresh])
  
  const getTokenPrice = (address) => {
    const rewardToken = tokens.find((row) => getAddress(row.address).toLowerCase() === address.toLowerCase())
    if (rewardToken && rewardToken.symbol === 'PEFI') return pefiPriceUsd
    if (rewardToken && rewardToken.symbol === 'iPEFI') return iPefiPriceUsd
    if (rewardToken && rewardToken.symbol === 'PNG') return pngPriceUsd
    if (rewardToken && rewardToken.symbol === 'JOE') return joePriceUsd
    if (rewardToken && rewardToken.symbol === 'LYD') return lydPriceUsd
    if (rewardToken && rewardToken.symbol === 'QI') return qiPriceUsd
    if (rewardToken && rewardToken.symbol === 'Sushi.e') return sushiPriceUsd
    if (rewardToken && rewardToken.symbol === 'AVAX') return avaxPriceUsd
    return 1
  }

  const getTokenSymbol = (address) => {
    const rewardToken = tokens.find((row) => getAddress(row.address).toLowerCase() === address.toLowerCase())
    return rewardToken.symbol
  }

  const getRewardsArray = () => {
    const _rewardTokens = {}
    v2Farms.map((row) => {
      if (row.userData && getBalanceNumber(row.userData.stakedBalance) > 0) {
        const { userPendingTokens } = row.userData

        userPendingTokens.map((row1) => {
          const { address, amount } = row1
          if (_rewardTokens[address]) {
            _rewardTokens[address] += getBalanceNumber(amount)
          } else {
            _rewardTokens[address] = getBalanceNumber(amount)
          }
          return row1
        })
      }
      return row
    })

    let _rewards = []
    let _totalRewardInUsd = 0
    let _rewardString = ''
    // eslint-disable-next-line no-restricted-syntax
    for (const [address, amount] of Object.entries(_rewardTokens)) {
      const tokenRewardInUsd = Number(amount) * Number(getTokenPrice(address))
      _totalRewardInUsd += tokenRewardInUsd
      if (Number(amount) > 0) {
        _rewardString += `${Number(amount).toFixed(0)} ${getTokenSymbol(address)}, `
      }

      _rewards = [
        ..._rewards,
        {
          address,
          amount,
          tokenPrice: getTokenPrice(address),
          tokenRewardInUsd,
          symbol: getTokenSymbol(address),
        },
      ]
    }

    return { rewards: _rewards, totalRewardInUsd: _totalRewardInUsd, rewardString: _rewardString.slice(0, -2) }
  }

  const { totalRewardInUsd, rewardString } = getRewardsArray()

  return (
    <StyledFarmStakingCard>
      <StyledCardHeader>
        <Title size="xl" mb="0px" color="primary">
          {TranslateString(542, 'Farms & Staking')}
        </Title>
      </StyledCardHeader>
      <StyledCardBody>
        <Flex flexDirection={isMobile ? 'column' : 'row'}>
          <PefiPrices flexDirection="column" flex={3}>
            <Flex mt="12px">
              <Flex flexDirection="column" flex={1}>
                <Label>{TranslateString(546, 'PEFI in Wallet')}:</Label>
                <PefiWalletBalance />
              </Flex>
              <Flex flexDirection="column" flex={1}>
                <Label>{TranslateString(544, 'iPEFI In Wallet')}:</Label>
                {account ?
                  <BalanceText>
                    {`${getBalanceNumber(stakedBalance).toFixed(2)}`}
                  </BalanceText>
                  :
                  <Text color="textDisabled" style={{ lineHeight: '32px' }}>
                    {TranslateString(298, 'Locked')}
                  </Text>
                }
                {/* <PefiHarvestBalance /> */}
              </Flex>
            </Flex>
            <Flex mt="36px" mb="20px">
              <Flex flexDirection="column" flex={1}>
                <Label>{TranslateString(544, 'Rewards to Harvest')}:</Label>
                <PefiHarvestBalance value={totalRewardInUsd} detailedValue={rewardString} />
              </Flex>
              <Flex flexDirection="column" flex={1}>
                <Label>{TranslateString(546, 'iPEFI/PEFI Ratio')}:</Label>
                <BalanceText>                
                  {`${Number(iPefiToPefiRatio.toFixed(2))} PEFI`}
                </BalanceText>
                {/* <PefiWalletBalance /> */}
              </Flex>
            </Flex>
          </PefiPrices>
          <StyledFlex flexDirection="column" flex={5}>
            <Flex mt="12px" mb="20px">
              <InfoCard title="In Farms" value={`${iglooApr ? roundDown((iglooApr * 100), 0) : 0}%`} unit="APR" subTitle="Up to" />
              <InfoCard title="Stake PEFI" value={`${roundDown(avgYearlyApr, 0)}%`} unit="APR" subTitle="Earn" bgColor="#392771" />
              <InfoCard title="Launchpad" value="860%" unit="ROI" subTitle="Up to" />
            </Flex>
            {account ? (
              <StyledButton disabled={pendingTx} id="harvest-pefi" onClick={harvestAllFarms} scale="md">
                {TranslateString(532, `Harvest All`)}
              </StyledButton>
            ) : (
              <StyledUnlockButton fullWidth isHomeButton />
            )}
          </StyledFlex>
        </Flex>
      </StyledCardBody>
    </StyledFarmStakingCard>
  )
}

export default FarmedStakingCard
